import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Spinner } from '@components/ui';
import { useProjectStore } from '@store';
import {
  useTaskStatuses,
  useTransitionRules,
  useCreateTransitionRule,
  useDeleteTransitionRule,
  useBulkUpdateTransitionRules,
} from '@hooks';

// Individual cell in the transition matrix
function TransitionCell({ isAllowed, onClick, isLoading, isSameStatus }) {
  if (isSameStatus) {
    return (
      <div
        style={{
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f1f5f9',
          borderRadius: '8px',
          color: '#94a3b8',
          fontSize: '16px',
        }}
      >
        -
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={isLoading}
      style={{
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isAllowed ? '#dcfce7' : '#f8fafc',
        border: isAllowed ? '2px solid #22c55e' : '2px solid #e2e8f0',
        borderRadius: '8px',
        cursor: isLoading ? 'wait' : 'pointer',
        transition: 'all 0.15s ease',
        opacity: isLoading ? 0.6 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isLoading) {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {isAllowed ? (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="#22c55e">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="#cbd5e1">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      )}
    </button>
  );
}

TransitionCell.propTypes = {
  isAllowed: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isSameStatus: PropTypes.bool,
};

// Status chip component
function StatusChip({ status, compact = false }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: compact ? '4px 8px' : '6px 12px',
        borderRadius: '6px',
        background: status.bgColor,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: status.color,
        }}
      />
      <span
        style={{
          fontSize: compact ? '11px' : '12px',
          fontWeight: '500',
          color: status.color,
        }}
      >
        {status.label}
      </span>
    </div>
  );
}

StatusChip.propTypes = {
  status: PropTypes.shape({
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    bgColor: PropTypes.string.isRequired,
  }).isRequired,
  compact: PropTypes.bool,
};

export default function TransitionRulesEditor() {
  const { selectedProject } = useProjectStore();
  const projectId = selectedProject?.id;

  const { data: statusesData, isLoading: statusesLoading } = useTaskStatuses(projectId);
  const { data: rulesData, isLoading: rulesLoading } = useTransitionRules(projectId);
  const createRule = useCreateTransitionRule();
  const deleteRule = useDeleteTransitionRule();
  const bulkUpdate = useBulkUpdateTransitionRules();

  const statuses = statusesData?.data || [];
  const rules = rulesData?.data || [];

  // Create a map for quick lookup: { fromStatusId: { toStatusId: ruleId } }
  const ruleMatrix = useMemo(() => {
    const matrix = {};
    rules.forEach((rule) => {
      if (!matrix[rule.fromStatusId]) {
        matrix[rule.fromStatusId] = {};
      }
      matrix[rule.fromStatusId][rule.toStatusId] = rule.id;
    });
    return matrix;
  }, [rules]);

  const handleCellClick = async (fromStatus, toStatus) => {
    const existingRuleId = ruleMatrix[fromStatus.id]?.[toStatus.id];

    if (existingRuleId) {
      await deleteRule.mutateAsync(existingRuleId);
    } else {
      await createRule.mutateAsync({
        projectId,
        fromStatusId: fromStatus.id,
        toStatusId: toStatus.id,
      });
    }
  };

  const handleAllowAll = async () => {
    const newRules = [];
    statuses.forEach((fromStatus) => {
      statuses.forEach((toStatus) => {
        if (fromStatus.id !== toStatus.id) {
          newRules.push({
            fromStatusId: fromStatus.id,
            toStatusId: toStatus.id,
          });
        }
      });
    });
    await bulkUpdate.mutateAsync({ projectId, rules: newRules });
  };

  const handleForwardOnly = async () => {
    const newRules = [];
    statuses.forEach((fromStatus, fromIndex) => {
      statuses.forEach((toStatus, toIndex) => {
        // Only allow forward transitions (to a later status in the order)
        if (toIndex > fromIndex) {
          newRules.push({
            fromStatusId: fromStatus.id,
            toStatusId: toStatus.id,
          });
        }
      });
    });
    await bulkUpdate.mutateAsync({ projectId, rules: newRules });
  };

  const handleClearAll = async () => {
    if (!window.confirm('¿Eliminar todas las reglas? Esto permitirá cualquier transición.')) return;
    await bulkUpdate.mutateAsync({ projectId, rules: [] });
  };

  if (!projectId) {
    return (
      <Card padding="lg">
        <p style={{ color: '#64748b', textAlign: 'center' }}>
          Selecciona un proyecto para configurar las reglas de transición
        </p>
      </Card>
    );
  }

  if (statusesLoading || rulesLoading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  if (statuses.length === 0) {
    return (
      <Card padding="lg">
        <p style={{ color: '#64748b', textAlign: 'center' }}>
          Primero configura los estados del Kanban en la pestaña anterior
        </p>
      </Card>
    );
  }

  const isLoading = createRule.isPending || deleteRule.isPending || bulkUpdate.isPending;

  return (
    <Card padding="lg">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
          Reglas de Transición
        </h2>
        <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
          Define qué movimientos están permitidos en el Kanban. Click en una celda para permitir/bloquear la transición.
        </p>

        {/* Info banner */}
        <div
          style={{
            padding: '12px 16px',
            background: rules.length > 0 ? '#fef3c7' : '#dbeafe',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '13px',
            color: rules.length > 0 ? '#92400e' : '#1e40af',
          }}
        >
          <span style={{ fontSize: '16px' }}>{rules.length > 0 ? '⚠️' : 'ℹ️'}</span>
          {rules.length > 0 ? (
            <span>
              <strong>{rules.length} reglas activas.</strong> Solo las transiciones marcadas con ✓ están permitidas.
            </span>
          ) : (
            <span>
              <strong>Sin restricciones.</strong> Todas las transiciones están permitidas. Agrega reglas para restringir el flujo.
            </span>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <Button
          variant="secondary"
          onClick={handleAllowAll}
          loading={bulkUpdate.isPending}
          style={{ fontSize: '13px' }}
        >
          Permitir todas
        </Button>
        <Button
          variant="secondary"
          onClick={handleForwardOnly}
          loading={bulkUpdate.isPending}
          style={{ fontSize: '13px' }}
        >
          Solo hacia adelante
        </Button>
        <Button
          variant="secondary"
          onClick={handleClearAll}
          loading={bulkUpdate.isPending}
          style={{ fontSize: '13px', color: rules.length > 0 ? '#ef4444' : undefined }}
        >
          Quitar restricciones
        </Button>
      </div>

      {/* Transition Matrix */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: '4px' }}>
          <thead>
            <tr>
              <th
                style={{
                  padding: '8px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#64748b',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                De ↓ / A →
              </th>
              {statuses.map((status) => (
                <th key={status.id} style={{ padding: '4px' }}>
                  <StatusChip status={status} compact />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statuses.map((fromStatus) => (
              <tr key={fromStatus.id}>
                <td style={{ padding: '4px' }}>
                  <StatusChip status={fromStatus} compact />
                </td>
                {statuses.map((toStatus) => (
                  <td key={toStatus.id} style={{ padding: '4px' }}>
                    <TransitionCell
                      isAllowed={!!ruleMatrix[fromStatus.id]?.[toStatus.id]}
                      onClick={() => handleCellClick(fromStatus, toStatus)}
                      isLoading={isLoading}
                      isSameStatus={fromStatus.id === toStatus.id}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div
        style={{
          marginTop: '24px',
          padding: '12px',
          background: '#f8fafc',
          borderRadius: '8px',
          display: 'flex',
          gap: '24px',
          fontSize: '12px',
          color: '#64748b',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '24px',
              height: '24px',
              background: '#dcfce7',
              border: '2px solid #22c55e',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="#22c55e">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <span>Transición permitida</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '24px',
              height: '24px',
              background: '#f8fafc',
              border: '2px solid #e2e8f0',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="#cbd5e1">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <span>Transición bloqueada</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '24px',
              height: '24px',
              background: '#f1f5f9',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              fontSize: '14px',
            }}
          >
            -
          </div>
          <span>Mismo estado</span>
        </div>
      </div>
    </Card>
  );
}

TransitionRulesEditor.propTypes = {};
